// accessGate.ts — КЛИЕНТСКОЕ СОСТОЯНИЕ ГЕЙТИНГА ДОСТУПА (подтверждение телефона).
//
// Источник — серверный отказ 412 (CodeFailedPrecondition) с метаданными
// require/action (internal/accessgate). Клиент НЕ решает, что запрещено: он
// только показывает баннер «Подтвердите номер телефона» и ведёт в существующий
// экран подтверждения. Оболочка (навигация/разделы) остаётся видимой.
import { create } from 'zustand';

export interface AccessGateNotice {
  require: string;
  action: string;
  target: string;
}

interface AccessGateState {
  notice: AccessGateNotice | null;
  /** Зафиксировать отказ сервера (412 с метаданными). */
  mark: (notice: AccessGateNotice) => void;
  /** Снять баннер (номер подтверждён/политика выключена/выход). */
  clear: () => void;
}

export const useAccessGateStore = create<AccessGateState>((set) => ({
  notice: null,
  mark: (notice) => set({ notice }),
  clear: () => set({ notice: null }),
}));

/** Разбор отказа RPC: 412 + require → уведомление. Не 412/без require — null. */
export function parseAccessGateError(error: unknown): AccessGateNotice | null {
  const anyE = error as {
    code?: unknown;
    rawMessage?: unknown;
    metadata?: { get?: (k: string) => string | null };
  } | null;
  if (!anyE) return null;
  const code = String(anyE.code ?? '');
  const isFailedPrecondition =
    anyE.code === 9 ||
    /failed[_-]?precondition/i.test(code) ||
    /failedprecondition/i.test(String(anyE.rawMessage ?? ''));
  if (!isFailedPrecondition) return null;
  const get = (k: string): string => {
    try {
      const v = anyE.metadata?.get?.(k);
      return typeof v === 'string' ? v : '';
    } catch {
      return '';
    }
  };
  let require = get('require');
  let action = get('action');
  let target = get('target');
  if (!require) {
    const raw = get('x-hydra-access-gate');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<AccessGateNotice>;
        require = String(parsed.require || '');
        action = String(parsed.action || '');
        target = String(parsed.target || '');
      } catch {
        /* метаданные не JSON — ниже вернём null */
      }
    }
  }
  if (!require) return null;
  return { require, action: action || 'verify_phone', target };
}
